import React, { useCallback, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useObjectState } from '../../../hooks/useObjectState';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useAxios } from '../../../hooks/useAxios';
import { NOTIFICATION_SUBMIT_INBOUND } from '../../../api/api';
import { showNotification } from '../../../redux/notifications/actions';
import { filterConnectedWarehouses } from '../Helpers';
import { getWarehouseConnections } from '../../../redux/warehouses/actions';
import useOutsideOfElementActions from '../../../hooks/useOutsideOfElementActions';
import { isOperableArray } from '../../../helpers/other';
import { format } from 'date-fns';
import { getListUrlFromPath, paths } from '../../../routes/constants';
import SubmitInboundFeedbackOnNotif from '../Components/SubmitInboundFeedbackOnNotif';

const initialState = {
  plannedArrivalDateOpen: false,
  isFetchingWarehouses: false,
};

const useScheduleInboundController = ({ data, onCloseNotification, onClose }) => {
  const [state, setState] = useObjectState(initialState);
  const history = useHistory();
  const warehouses = useSelector((state) => state?.warehouses?.items);
  const warehousesOptions = warehouses?.filter(filterConnectedWarehouses);
  const dispatch = useDispatch();

  const formikRef = useRef(null);
  const datePickerRef = useRef(null);
  const { callAxios: submitInbound, isLoading: isSubmittingInbound } = useAxios({
    method: 'post',
    url: NOTIFICATION_SUBMIT_INBOUND,
    callOnMount: false,
    shareNonPrimitiveError: true,
    onSuccess: () => onClose(),
    onError: (err) => {
      const errors = err?.data?.errors;

      dispatch(
        showNotification(
          errors?.length ? (
            <ul>{errors?.length && errors?.map((error, index) => <li key={index}>{error}</li>)}</ul>
          ) : (
            'Inbound request failed'
          ),
          'error'
        )
      );
    },
  });

  const setPlannedArrivalDateOpen = (value) => setState({ plannedArrivalDateOpen: value });
  const handleSkuDelete = useCallback((sku) => {
    const { setValues, values } = formikRef.current;
    let newProducts = isOperableArray(values?.products) ? [...values.products] : [];

    //remove sku from products array
    newProducts = newProducts.filter((item) => item.sku !== sku);

    setValues({
      ...values,
      products: newProducts,
    });
  }, []);

  const handleSubmit = useCallback(
    async (is_draft) => {
      const validateForm = formikRef?.current?.validateForm;
      const values = formikRef?.current?.values;
      validateForm().then(async (err) => {
        const errorFields = Object.keys(err);

        if (isOperableArray(errorFields) && errorFields.length) {
          return;
        }
        const { warehouse_id, planned_arrival_date, consignment_number, products } = values;

        const payload = {
          data: {
            warehouse_id,
            planned_arrival_date,
            consignment_number,
            consignment_date: format(new Date(), 'yyyy-LL-dd'),
            is_draft,
            address: '-',
            products,
          },
        };

        const submitInboundResponse = await submitInbound(payload);

        //acknowledge notification after submit
        //3rd arg for preventing acknowledge notif
        onCloseNotification({
          notificationId: data?.notificationId,
        });

        const consignmentId = submitInboundResponse?.data?.inbound_consignment_id;

        const asnRoute = getListUrlFromPath(paths.ADVANCE_SHIPPING_NOTES_LISTING, consignmentId);

        history.push(asnRoute);

        // added timeout for prevent dismissing on page redirect
        setTimeout(() => {
          dispatch(
            showNotification(
              <SubmitInboundFeedbackOnNotif
                successMessage={submitInboundResponse?.message || 'Success'}
                notificationData={data}
              />,
              'success'
            )
          );
        }, 500);
      });
    },
    [data, dispatch, submitInbound, onCloseNotification, history]
  );

  useOutsideOfElementActions(datePickerRef, () => {
    setPlannedArrivalDateOpen(false);
  });

  useEffect(() => {
    if (!warehouses?.length) {
      setState({ isFetchingWarehouses: true });
      dispatch(getWarehouseConnections()).then(() => setState({ isFetchingWarehouses: false }));
    }
  }, [warehouses, dispatch, setState]);

  const handlers = { handleSkuDelete, handleSubmit, setPlannedArrivalDateOpen };

  return [{ ...state, isSubmittingInbound, warehousesOptions }, handlers, { formikRef, datePickerRef }];
};

export default useScheduleInboundController;
